import { useId, useState } from 'react';
import { Box, ButtonBase, Chip, Collapse, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import type { UnplacedCalendarWork } from '@/features/calendar/calendarSegments';

function itemLabel(item: UnplacedCalendarWork): string {
  return [item.workOrderNumber, item.vehicleRegNumber, item.title]
    .filter((part) => part != null && part !== '')
    .join(' · ');
}

/**
 * Compact needs-placement list for floating work with no mechanic queue
 * placement (#2686). Timed calendar rows never synthesize a slot for these.
 * Collapsed by default so a long list does not push the grid down (#3319).
 */
export function UnplacedWorkBucket({
  items,
  onSelect,
}: {
  items: UnplacedCalendarWork[];
  onSelect: (bookingId: string) => void;
}) {
  const { t } = useTranslation('calendar');
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  if (items.length === 0) return null;

  return (
    <Box
      data-testid="unplaced-work-bucket"
      sx={{
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: 'warning.light',
        bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
      }}
    >
      <ButtonBase
        onClick={() => { setExpanded((open) => !open); }}
        aria-expanded={expanded}
        aria-controls={listId}
        data-testid="unplaced-work-toggle"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 0.5 }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: (theme) => theme.transitions.create('transform'),
          }}
        />
        <Typography variant="caption" fontWeight={700}>
          {t('unplaced.title', { count: items.length })}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Box
          id={listId}
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, pt: 0.75 }}
        >
          {items.map((item) => {
            const label = itemLabel(item);
            return (
              <Chip
                key={item.bookingId}
                size="small"
                variant="outlined"
                label={label}
                onClick={() => { onSelect(item.bookingId); }}
                aria-label={t('unplaced.openWorkOrder', {
                  number: item.workOrderNumber || item.vehicleRegNumber || label,
                })}
                data-testid={`unplaced-work-${item.bookingId}`}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
